<template>
  <div class="shell">
    <Watermark />
    <aside class="side">
      <router-link to="/" class="logo">
        <img class="mark" src="/logo.png" alt="" width="28" height="28" draggable="false" />
        算力百科
      </router-link>
      <nav class="workspaces">
        <router-link
          v-for="item in menus"
          :key="item.to"
          v-slot="{ href, navigate }"
          :to="item.to"
          custom
        >
          <a
            :href="href"
            class="ws"
            :class="{ on: item.match($route) }"
            @click="navigate"
          ><Icon :name="item.icon" />{{ item.label }}</a>
        </router-link>
      </nav>
      <div class="side-foot">
        <div class="follow-wrap">
          <button type="button" class="follow-entry" @click="followOpen = !followOpen">关注送积分</button>
          <FollowBonusPanel
            v-if="followOpen"
            :authenticated="authenticated"
            @close="followOpen = false"
            @credited="refreshMe"
          />
        </div>
        <router-link to="/agent" class="desktop-entry">
          <span class="desktop-title">下载桌面版</span>
          <span class="desktop-sub">总调度者 · 算力选型</span>
        </router-link>
        <div v-if="authenticated" class="avatar-wrap" @click.stop>
          <button
            type="button"
            class="avatar-row"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            :title="displayName"
            @click="menuOpen = !menuOpen"
          >
            <span class="avatar">{{ avatarText }}</span>
            <span class="avatar-name">{{ displayName }}</span>
          </button>
          <ul v-if="menuOpen" class="avatar-menu">
            <li class="who">
              <strong>{{ displayName }}</strong>
              <small v-if="balances">积分 {{ balances.points }}</small>
            </li>
            <li>
              <router-link to="/account" @click="menuOpen = false">我的</router-link>
            </li>
            <li v-if="user">
              <button type="button" @click="openLedger">积分流水</button>
            </li>
            <li>
              <button type="button" @click="logout">退出</button>
            </li>
          </ul>
        </div>
        <router-link v-else to="/account" class="login-link">登录</router-link>
      </div>
    </aside>

    <div class="body">
      <CreditsPopover
        :open="ledgerOpen"
        :nickname="user && (user.nickname || user.email)"
        :email="user && user.email"
        :balances="balances"
        @close="ledgerOpen = false"
      />
      <main class="operation" :class="{ wide }">
        <slot />
      </main>
    </div>
  </div>
</template>

<script>
import Icon from '../components/Icon.vue'
import Watermark from '../components/Watermark.vue'
import CreditsPopover from '../components/CreditsPopover.vue'
import FollowBonusPanel from '../components/FollowBonusPanel.vue'
import { fetchMe } from '../api/platformService'
import { clearSession, sessionRole, setSession } from '../utils/session'
import { notifyAuthChanged, onAuthChanged, onOpenFollow } from '../utils/authBus'

export default {
  name: 'AppShell',
  components: { Icon, Watermark, CreditsPopover, FollowBonusPanel },
  props: {
    wide: { type: Boolean, default: false }
  },
  data() {
    return {
      authenticated: Boolean(sessionRole()),
      role: sessionRole() || '',
      user: null,
      balances: null,
      ledgerOpen: false,
      followOpen: false,
      menuOpen: false,
      menus: [
        {
          to: '/',
          label: '查参数',
          icon: 'search',
          match: (route) => (route.path === '/' || route.path === '/compare') && !['price', 'bom', 'bid', 'advise'].includes(route.query.mode)
        },
        {
          to: { path: '/', query: { mode: 'price' } },
          label: '卡价格',
          icon: 'price',
          match: (route) => route.query.mode === 'price'
        },
        {
          to: { path: '/', query: { mode: 'bom' } },
          label: '服务器估价',
          icon: 'server',
          match: (route) => route.query.mode === 'bom'
        },
        {
          to: { path: '/', query: { mode: 'bid' } },
          label: '招投标分析',
          icon: 'file',
          match: (route) => route.query.mode === 'bid'
        },
        {
          to: { path: '/', query: { mode: 'advise' } },
          label: '选型指导',
          icon: 'spark',
          match: (route) => route.query.mode === 'advise'
        },
        {
          to: '/forum',
          label: '供需论坛',
          icon: 'forum',
          match: (route) => route.path === '/forum'
        },
        {
          to: '/articles',
          label: '行业资讯',
          icon: 'book',
          match: (route) => route.path === '/articles' || route.path.startsWith('/articles/')
        }
      ],
      offAuth: null,
      offFollow: null
    }
  },
  computed: {
    displayName() {
      if (this.user) return this.user.nickname || this.user.email
      if (this.role === 'admin') return '管理员'
      return '已登录'
    },
    avatarText() {
      return String(this.displayName).trim().slice(0, 1).toUpperCase()
    }
  },
  async mounted() {
    await this.refreshMe()
    this.offAuth = onAuthChanged(() => this.refreshMe())
    this.offFollow = onOpenFollow(() => {
      this.followOpen = true
    })
    document.addEventListener('click', this.onDocClick)
  },
  beforeUnmount() {
    if (this.offAuth) this.offAuth()
    if (this.offFollow) this.offFollow()
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    async refreshMe() {
      try {
        const me = await fetchMe()
        if (!me.authenticated) {
          if (sessionRole()) clearSession()
          this.authenticated = false
          this.role = ''
          this.user = null
          this.balances = null
          return
        }
        if (me.role === 'visitor') {
          clearSession()
          this.authenticated = false
          this.role = ''
          this.user = null
          this.balances = null
          return
        }
        if (me.token && me.role) setSession(me.token, me.role)
        this.authenticated = true
        this.role = me.role || ''
        this.user = me.user || null
        this.balances = me.balances || null
      } catch (e) {
        this.authenticated = Boolean(sessionRole())
        if (!this.authenticated) {
          this.user = null
          this.balances = null
        }
      }
    },
    onDocClick(event) {
      if (this.followOpen) {
        const wrap = this.$el.querySelector('.follow-wrap')
        if (wrap && !wrap.contains(event.target)) this.followOpen = false
      }
      if (this.menuOpen) {
        const menu = this.$el.querySelector('.avatar-wrap')
        if (menu && !menu.contains(event.target)) this.menuOpen = false
      }
    },
    openLedger() {
      this.menuOpen = false
      this.ledgerOpen = true
    },
    logout() {
      clearSession()
      this.authenticated = false
      this.role = ''
      this.user = null
      this.balances = null
      this.ledgerOpen = false
      this.followOpen = false
      this.menuOpen = false
      notifyAuthChanged()
      if (this.$route.path !== '/') this.$router.push('/')
      else this.$router.go(0)
    }
  }
}
</script>

<style scoped>
.shell {
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 100vh;
  background: #f7f8fa;
  color: #1d2129;
}

.side {
  position: sticky;
  top: 0;
  z-index: 30;
  width: 200px;
  flex: 0 0 200px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 12px;
  background: #fff;
  border-right: 1px solid #e5e6eb;
}

.body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.logo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1d2129;
  text-decoration: none;
  font-weight: 700;
  white-space: nowrap;
}

.mark {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.workspaces {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.ws {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  color: #4e5969;
  text-decoration: none;
  font-size: 14px;
}

.ws.on {
  color: #165dff;
  background: #e8f3ff;
  font-weight: 600;
}

.side-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.follow-wrap { position: relative; }

.follow-wrap :deep(.follow-pop) {
  top: auto;
  bottom: 0;
  left: calc(100% + 8px);
  right: auto;
}

.follow-entry {
  width: 100%;
  border: 1px solid #ffcf8b;
  background: #fff7e8;
  color: #d25f00;
  border-radius: 10px;
  height: 36px;
  padding: 0 12px;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
}

.follow-entry:hover {
  border-color: #d25f00;
}

.desktop-entry {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  border: 1px solid #b7d0ff;
  background: linear-gradient(135deg, #e8f3ff 0%, #f0f7ff 100%);
  color: #0b3dcc;
  border-radius: 10px;
  padding: 8px 12px;
  text-decoration: none;
  line-height: 1.25;
}
.desktop-entry:hover {
  border-color: #165dff;
  background: #e8f3ff;
}
.desktop-title {
  font-size: 13px;
  font-weight: 700;
}
.desktop-sub {
  font-size: 11px;
  color: #4e5969;
}

.avatar-wrap { position: relative; }
.avatar-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  border-radius: 10px;
  padding: 4px;
  cursor: pointer;
  color: #1d2129;
}
.avatar-row:hover { background: #f2f3f5; }
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #165dff;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
}
.avatar-name {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.avatar-menu {
  position: absolute;
  left: calc(100% + 8px);
  bottom: 0;
  z-index: 40;
  margin: 0;
  padding: 6px;
  list-style: none;
  min-width: 168px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(29, 33, 41, 0.14);
}
.avatar-menu .who {
  display: grid;
  gap: 2px;
  padding: 8px 10px 10px;
  border-bottom: 1px solid #f2f3f5;
}
.avatar-menu .who strong {
  font-size: 13px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.avatar-menu .who small { color: #86909c; font-size: 12px; }
.avatar-menu a,
.avatar-menu button {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  color: #1d2129;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
}
.avatar-menu a:hover,
.avatar-menu button:hover { background: #f2f7ff; color: #165dff; }

.login-link {
  border: 1px solid #e5e6eb;
  background: #fff;
  color: #4e5969;
  border-radius: 10px;
  height: 36px;
  padding: 0 12px;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
  display: flex;
  align-items: center;
}

.operation {
  flex: 1 0 auto;
  max-width: 1080px;
  width: 100%;
  margin: 0 auto;
  padding: 28px 16px 48px;
  position: relative;
  z-index: 1;
}
.operation.wide {
  max-width: 1360px;
}

</style>
